/**
 * Back-compat for the pre-BrowserRouter URLs.
 *
 * The app used to run on HashRouter, so every link we ever put in a booking or
 * donation email — and every bookmark a devotee saved — looks like
 * `https://host/#/en/donate`. Those URLs still resolve to index.html, but
 * BrowserRouter reads the *path* (`/`), so without this they would all land on
 * the home page instead of their target.
 *
 * Translating `#/en/donate` → `/en/donate` before React mounts keeps every old
 * link working. Kept as a pure function so the mapping is unit-tested rather
 * than only exercised by hand in a browser.
 */

/**
 * Returns the path a legacy hash URL should become, or null if `href` is not a
 * legacy hash URL and should be left alone.
 */
export function resolveLegacyHashUrl(href: string): string | null {
    const url = new URL(href);

    // Only `#/...` is a legacy route. A bare `#section` is an anchor and a
    // hash-less URL is already a clean path — neither should be touched.
    if (!url.hash.startsWith('#/')) return null;

    // Everything after the `#` is the old route, query string and all.
    const legacyRoute = url.hash.slice(1);
    const legacy = new URL(legacyRoute, url.origin);

    // A query string on the real URL (?utm_source=…) is worth keeping, but the
    // one carried inside the hash route wins on conflict — it was the one the
    // old router actually read.
    const params = new URLSearchParams(url.search);
    legacy.searchParams.forEach((value, key) => params.set(key, value));

    const search = params.toString();
    return `${legacy.pathname}${search ? `?${search}` : ''}${legacy.hash}`;
}

/**
 * Rewrites a legacy `#/...` URL in place. Uses replaceState so the dead hash
 * URL does not sit in history and send Back to the same redirect.
 */
export function applyLegacyHashRedirect(location: Location, history: History): void {
    const target = resolveLegacyHashUrl(location.href);
    if (target) history.replaceState(null, '', target);
}
